/**
 * Pure decision logic for whether GPU field-smoothing (mosaicGlCompositor.ts's
 * despeckle+separable-exact-Gaussian FBO pipeline) can run at all, versus
 * falling back to the existing CPU worker path (mosaicGlFieldSmoothing.ts,
 * mosaicSmoothClient.ts — kept fully intact as the fallback, not replaced).
 * Split out from the actual GL capability PROBING (which needs a real
 * WebGLRenderingContext/WebGL2RenderingContext and so can't run under
 * vitest's `environment: "node"`, same testability gap every other GL-
 * touching file in this client has — mosaicGlCompositor.ts's own doc
 * comments) so the DECISION itself — given capability facts as plain
 * booleans — is unit-testable without a browser.
 *
 * The blur passes need a render target with more precision than 8 bits
 * (accumulating many weighted 8-bit samples across 3 sequential passes in
 * an 8-bit intermediate would quantize/clip repeatedly, unlike the CPU
 * path's Float32Array intermediates) — RGBA16F is the target format
 * (mosaicGlCompositor.ts), renderable only when the browser/driver actually
 * supports it:
 *  - WebGL2: `EXT_color_buffer_float` alone covers rendering to RGBA16F
 *    (and R16F/RG16F/RGBA32F etc.) — one extension check.
 *  - WebGL1: needs `OES_texture_half_float` (to CREATE a half-float-typed
 *    texture at all) AND `EXT_color_buffer_half_float` (to actually
 *    RENDER into one) — two extensions.
 * Either way, extension presence is necessary but not sufficient on every
 * real device/driver — mosaicGlCompositor.ts's actual probing additionally
 * does an empirical `checkFramebufferStatus` after attaching a real
 * half-float texture, and folds that boolean in as `fboComplete` here
 * rather than trusting extension strings alone.
 */
export interface SmoothingCapabilities {
  hasWebGL2: boolean;
  /** WebGL2's `EXT_color_buffer_float`, or WebGL1's
   * `EXT_color_buffer_half_float` — whichever applies to the context this
   * was probed against. */
  hasFloatRenderTarget: boolean;
  /** WebGL1 only: `OES_texture_half_float` (needed to CREATE the texture at
   * all, distinct from rendering INTO one). Irrelevant/ignored when
   * `hasWebGL2` is true (WebGL2's texStorage2D(RGBA16F) needs no separate
   * "can this type of texture even be created" extension). */
  hasHalfFloatTexture: boolean;
  /** Empirical `checkFramebufferStatus(FRAMEBUFFER) === FRAMEBUFFER_COMPLETE`
   * after actually attaching a render-target texture of the chosen format —
   * the real authority; a browser can report the extensions above present
   * and still fail this on a specific device/driver. */
  fboComplete: boolean;
}

export type SmoothingPath = "gpu" | "cpu-fallback";

/** The single decision point: GPU smoothing requires EVERY relevant
 * capability to hold, not just "an extension string exists" — any missing
 * or unverified piece falls back to the CPU path, which is fully
 * functional (just slower for a first-visit frame) rather than a
 * degraded/broken experience. */
export function decideSmoothingPath(caps: SmoothingCapabilities): SmoothingPath {
  if (!caps.hasFloatRenderTarget) return "cpu-fallback";
  if (!caps.hasWebGL2 && !caps.hasHalfFloatTexture) return "cpu-fallback"; // WebGL1 needs BOTH extensions, not just the renderability one
  if (!caps.fboComplete) return "cpu-fallback"; // extensions present but the actual attach+complete probe failed
  return "gpu";
}
